import React from 'react'
import { graphql } from 'babel-plugin-relay/macro'
import { useIntl } from 'react-intl'
import { useFragment } from 'react-relay-offline'

import { ProductSkuAvailabilityRow } from './ProductSkuAvailabilityRow'
import type { ProductSkuAvailabilityTakeAway_ProductSkuOfferConnection$key } from './__generated__/ProductSkuAvailabilityTakeAway_ProductSkuOfferConnection.graphql'
import { useOrderForm } from '../../../../core/requests/checkout'
import { DeliveryChannelIcon } from '../../../../core/components'

const productSkuAvailabilityConnectionFragment = graphql`
  fragment ProductSkuAvailabilityTakeAway_ProductSkuOfferConnection on ProductSkuOfferConnection {
    edges {
      node {
        deliveryChannel
        stock
      }
    }
  }
`

export type ProductSkuAvailabilityTakeAwayProps = {
  busy?: boolean
  productSkuOfferConnectionRef: ProductSkuAvailabilityTakeAway_ProductSkuOfferConnection$key
}

export const ProductSkuAvailabilityTakeAway = ({
  busy,
  productSkuOfferConnectionRef,
}: ProductSkuAvailabilityTakeAwayProps) => {
  const { formatMessage, formatNumber } = useIntl()
  const { getCurrencyCode } = useOrderForm()

  const productSkuOffers = useFragment(
    productSkuAvailabilityConnectionFragment,
    productSkuOfferConnectionRef
  )

  const takeAwayOffer = productSkuOffers.edges.find(
    (offer) => offer.node.deliveryChannel === 'TAKE_AWAY'
  )

  const renderTakeAwayMessage = () => {
    if (!takeAwayOffer) {
      return formatMessage({ id: 'pdp.sku-availability.take-away.unavailable' })
    }

    const stockQuantity = formatNumber(takeAwayOffer?.node.stock ?? 0, {
      currency: getCurrencyCode(),
    })

    return formatMessage(
      { id: 'pdp.sku-availability.take-away.available' },
      { stockQuantity }
    )
  }

  return (
    <ProductSkuAvailabilityRow
      data-testid="product-sku-availability-take-away"
      icon={<DeliveryChannelIcon deliveryChannel="TAKE_AWAY" />}
      title={formatMessage({ id: 'pdp.sku-availability.take-away.title' })}
      message={renderTakeAwayMessage()}
      status={takeAwayOffer ? 'available' : 'unavailable'}
      busy={busy}
    />
  )
}
